import React from 'react';

import { useTranslation } from 'react-i18next';

export const LocationSection: React.FC = () => {
  const { t } = useTranslation();

  const categories = [
    { id: 'culture', icon: '🎭' },
    { id: 'adventure', icon: '🎢' },
    { id: 'dining', icon: '🍷' },
    { id: 'kids', icon: '👶' },
    { id: 'sport', icon: '🚴' },
    { id: 'shopping', icon: '🛍️' },
    { id: 'essentials', icon: '🚑' }
  ];

  return (
    <section className="section-padding bg-[var(--warm-white)]" id="location">
      <div className="container">
        <h2 className="section-title text-4xl font-bold text-center mb-12">{t('location.title')}</h2>

        {/* Map & Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 mb-12">
          <div className="location-card overflow-hidden warm-border">
            <div className="h-[400px] rounded-lg overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25580.4235506672!2d-4.5839657!3d36.5066821!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd72d809904dabdf%3A0xe6c9db907b59965!2sCalle%20Ca%C3%B1ete%20la%20Real%2039%2C%2029130%20Alhaur%C3%ADn%20de%20la%20Torre%2C%20M%C3%A1laga%2C%20Spain!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                allowFullScreen={true}
                loading="lazy"
                className="w-full h-full border-0"
                title="Villa Location Map"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Activities Grid */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-600 bg-white/50 inline-block px-4 py-2 rounded-full border border-[var(--terracotta-light)]">
            {t('location.linksNote')}
          </p>
        </div>
        <div className="md:hidden text-center mb-2 animate-pulse">
          <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
            <span>←</span> {t('location.swipeForMore') || 'Swipe'} <span>→</span>
          </p>
        </div>
        <div className="flex overflow-x-auto snap-x snap-mandatory pb-6 gap-4 px-4 -mx-4 md:grid md:grid-cols-2 md:grid-rows-none md:overflow-visible md:gap-6 md:px-0 md:mx-0 lg:grid-cols-3 xl:grid-cols-4 scrollbar-hide">
          {categories.map((cat) => {
            const items = t(`location.activities.${cat.id}.items`, { returnObjects: true }) as string[];
            const title = t(`location.activities.${cat.id}.title`);

            // Safety check to ensure we have content before rendering
            if (!Array.isArray(items)) return null;

            return (
              <div key={cat.id} className="location-card p-6 warm-shadow bg-white hover:shadow-lg transition-all duration-300 min-w-[85vw] snap-center md:min-w-0 md:w-auto h-full">
                <div className="flex items-center gap-3 mb-4 border-b border-[var(--terracotta-light)] pb-2">
                  <span className="text-2xl" role="img" aria-label={cat.id}>{cat.icon}</span>
                  <h3 className="text-xl font-bold text-[var(--primary)]">{title}</h3>
                </div>
                <ul className="space-y-3">
                  {items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1 text-[var(--terracotta)]">▪</span>
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
